// PlanPicker — grilla de planes seleccionables.
//
// Dos usos, con la misma card:
//   - Alta de cuenta (Signup): el catalogo completo, nada preseleccionado.
//   - Cambio de plan (Subscribe): el catalogo completo con el plan vigente
//     marcado con la chapita "Plan actual", y una opcion marcada de entrada
//     (`autoSelect`) para que pueda ir directo al boton. Que opcion se marca lo
//     decide lib/plan-selection.
//
// Trae los planes activos del partner (GET /partners/{id}/plans, ya ordenados
// por precio asc → free primero).
//
// Sin estado propio de seleccion: es controlado (value/onChange) para que el
// contenedor sepa que eligio sin levantar el estado dos veces.

import { useEffect, useRef, useState } from 'react';
import { fetchPlans, type Plan } from '@/lib/api';
import { planOptionsFor } from '@/lib/plan-selection';

interface PlanPickerProps {
  partnerId: string;
  value: string | null;
  onChange: (planId: string, plan: Plan) => void;
  /** Plan vigente del usuario: se muestra con la chapita "Plan actual". */
  currentPlanId?: string | null;
  /** `?plan_id=` de la OTT: gana sobre el recomendado si sigue disponible. */
  preferredPlanId?: string | null;
  /**
   * Si true, al terminar de cargar marca la opcion por defecto (una sola vez, y
   * solo si el contenedor todavia no tiene una elegida). Default false: en el
   * alta la eleccion arranca en blanco a proposito.
   */
  autoSelect?: boolean;
  /** Texto cuando no queda ningun plan para ofrecer. */
  emptyLabel?: string;
}

type PlanFeature = string | { label: string; included?: boolean };

function featureLabel(f: PlanFeature): string {
  return typeof f === 'string' ? f : f.label;
}

/**
 * `included: false` = la feature NO viene con este plan (se muestra tachada,
 * para contrastar contra el plan de arriba). Un string suelto o `included`
 * ausente = incluida.
 *
 * Hasta que el backoffice tuvo editor de features, esto no se podia configurar
 * y el render pintaba ✓ para todo. Ahora que el admin puede marcar "no
 * incluido", ignorarlo mostraria un ✓ en algo que el plan no da.
 */
function featureIncluded(f: PlanFeature): boolean {
  return typeof f === 'string' || f.included !== false;
}

function formatPrice(plan: Plan): { amount: string; suffix: string } {
  if (plan.price_amount === 0) return { amount: 'Gratis', suffix: '' };
  const amount = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: plan.price_currency,
    maximumFractionDigits: 0,
  }).format(plan.price_amount);
  const periodLabel =
    plan.billing_period === 'monthly'
      ? '/mes'
      : plan.billing_period === 'yearly'
        ? '/año'
        : '/semana';
  return { amount, suffix: periodLabel };
}

export default function PlanPicker({
  partnerId,
  value,
  onChange,
  currentPlanId = null,
  preferredPlanId = null,
  autoSelect = false,
  emptyLabel = 'No hay planes disponibles por ahora.',
}: PlanPickerProps): JSX.Element {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // La preseleccion se dispara una sola vez: si el usuario elige otra cosa (o
  // vuelve de la pantalla de pago), no queremos que la card se re-marque sola.
  const autoSelected = useRef(false);

  useEffect(() => {
    if (!partnerId) return;
    let alive = true;
    fetchPlans(partnerId)
      .then((p) => {
        if (alive) setPlans(p);
      })
      .catch(() => {
        if (alive) setError('No pudimos cargar los planes. Probá de nuevo en un momento.');
      });
    return () => {
      alive = false;
    };
  }, [partnerId]);

  const { options, defaultPlanId } = planOptionsFor({
    plans: plans ?? [],
    currentPlanId,
    preferredPlanId,
  });

  useEffect(() => {
    if (!autoSelect || autoSelected.current || plans === null || value !== null) return;
    const preselected = options.find((p) => p.plan_id === defaultPlanId);
    if (!preselected) return;
    autoSelected.current = true;
    onChange(preselected.plan_id, preselected);
    // `options`/`onChange` se recrean en cada render; el guard de arriba (ref +
    // plans !== null) es lo que hace idempotente al efecto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelect, plans, value, defaultPlanId]);

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error"
      >
        {error}
      </div>
    );
  }

  if (!plans) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="h-32 animate-pulse rounded-xl bg-text/5" />
        <div className="h-32 animate-pulse rounded-xl bg-text/5" />
      </div>
    );
  }

  if (options.length === 0) {
    return <p className="text-sm text-text-muted">{emptyLabel}</p>;
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Planes">
      {options.map((plan) => {
        const selected = value === plan.plan_id;
        const isCurrent = plan.plan_id === currentPlanId;
        const price = formatPrice(plan);
        return (
          <button
            key={plan.plan_id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(plan.plan_id, plan)}
            className={`relative flex flex-col rounded-xl border p-4 text-left transition ${
              selected
                ? 'border-primary ring-2 ring-primary ring-offset-1 ring-offset-background'
                : 'border-border hover:border-primary/50'
            }`}
          >
            {/* Una sola chapita por card. "Plan actual" gana sobre
                "Recomendado": saber que tiene hoy le importa mas al usuario que
                la sugerencia del partner, y el plan vigente puede ser
                justamente el recomendado. La de recomendado sale del plan
                marcado en el backoffice; sin recomendado configurado no hay
                chapita. */}
            {isCurrent ? (
              <span className="absolute right-3 top-3 rounded-full bg-text/10 px-2 py-0.5 text-[11px] font-medium text-text">
                Plan actual
              </span>
            ) : plan.recommended ? (
              <span className="absolute right-3 top-3 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                Recomendado
              </span>
            ) : null}
            <span className="text-sm font-medium text-text-muted">{plan.display_name}</span>
            <span className="mt-1 flex items-baseline gap-1">
              <span className="text-2xl font-semibold text-text">{price.amount}</span>
              {price.suffix ? (
                <span className="text-sm text-text-muted">{price.suffix}</span>
              ) : null}
            </span>
            <ul className="mt-3 space-y-1.5">
              {plan.features.map((f, i) => {
                const included = featureIncluded(f);
                return (
                  <li
                    key={i}
                    className={`flex items-start gap-2 text-sm ${
                      included ? 'text-text' : 'text-text-muted'
                    }`}
                  >
                    <span
                      aria-hidden
                      className={`mt-0.5 ${included ? 'text-primary' : 'text-text-muted'}`}
                    >
                      {included ? '✓' : '✕'}
                    </span>
                    <span className={included ? undefined : 'line-through'}>
                      {featureLabel(f)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </button>
        );
      })}
    </div>
  );
}
